import React, { useEffect, useState } from "react";

import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	MenuItem,
	TextField,
	Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

export const REASONS = ["cliente", "modelo", "erro", "ventana", "hostilidade"];

const lines = text =>
	String(text || "")
		.split("\n")
		.map(l => l.trim())
		.filter(Boolean);

const toForm = scenario => {
	const d = scenario?.definition;
	const e = d?.expect || {};
	return {
		name: scenario?.name || "",
		turns: (d?.turns || []).join("\n"),
		businessHours: d?.businessHours || "open",
		handoff: e.handoff || "any",
		reasons: e.reasons || [],
		mustContain: (e.mustContain || []).join("\n"),
		mustNotContain: (e.mustNotContain || []).join("\n"),
		minReplies: e.minReplies ?? "",
		maxReplies: e.maxReplies ?? "",
		maxReplyChars: e.maxReplyChars ?? "",
		replyTimeoutSec: d?.replyTimeoutSec ?? 60,
	};
};

const num = v => (v === "" || v === null ? undefined : Number(v));

const ScenarioModal = ({ open, scenario, onClose, onSaved }) => {
	const [form, setForm] = useState(toForm(null));
	const [saving, setSaving] = useState(false);
	const editingId = scenario && !scenario.builtin ? scenario.key.split(":")[1] : null;

	useEffect(() => {
		if (open) setForm(toForm(scenario));
	}, [open, scenario]);

	const set = patch => setForm(prev => ({ ...prev, ...patch }));

	const save = async () => {
		const payload = {
			name: form.name,
			definition: {
				turns: lines(form.turns),
				businessHours: form.businessHours,
				replyTimeoutSec: num(form.replyTimeoutSec),
				expect: {
					handoff: form.handoff,
					reasons: form.handoff === "forbidden" ? [] : form.reasons,
					mustContain: lines(form.mustContain),
					mustNotContain: lines(form.mustNotContain),
					minReplies: num(form.minReplies),
					maxReplies: num(form.maxReplies),
					maxReplyChars: num(form.maxReplyChars),
				},
			},
		};
		setSaving(true);
		try {
			if (editingId) await api.put(`/lab/scenarios/${editingId}`, payload);
			else await api.post("/lab/scenarios", payload);
			toast.success(i18n.t("lab.scenarioSaved"));
			onSaved();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const toggleReason = reason =>
		set({
			reasons: form.reasons.includes(reason)
				? form.reasons.filter(r => r !== reason)
				: [...form.reasons, reason],
		});

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
			<DialogTitle>
				{editingId ? i18n.t("lab.modal.titleEdit") : i18n.t("lab.modal.titleNew")}
			</DialogTitle>
			<DialogContent dividers>
				<TextField
					label={i18n.t("lab.modal.name")}
					value={form.name}
					onChange={e => set({ name: e.target.value })}
					fullWidth
					variant="outlined"
					margin="dense"
					autoFocus
				/>
				<TextField
					label={i18n.t("lab.modal.turns")}
					helperText={i18n.t("lab.modal.turnsHelp")}
					value={form.turns}
					onChange={e => set({ turns: e.target.value })}
					fullWidth
					multiline
					rows={4}
					variant="outlined"
					margin="dense"
				/>
				<TextField
					select
					label={i18n.t("lab.modal.hours")}
					helperText={i18n.t("lab.modal.hoursHelp")}
					value={form.businessHours}
					onChange={e => set({ businessHours: e.target.value })}
					fullWidth
					variant="outlined"
					margin="dense"
				>
					{["open", "closed", "real"].map(v => (
						<MenuItem key={v} value={v}>
							{i18n.t(`lab.hours.${v}`)}
						</MenuItem>
					))}
				</TextField>

				<Typography variant="subtitle2" style={{ marginTop: 16 }}>
					{i18n.t("lab.modal.expectTitle")}
				</Typography>
				<TextField
					select
					label={i18n.t("lab.modal.handoff")}
					value={form.handoff}
					onChange={e => set({ handoff: e.target.value })}
					fullWidth
					variant="outlined"
					margin="dense"
				>
					{["any", "required", "forbidden"].map(v => (
						<MenuItem key={v} value={v}>
							{i18n.t(`lab.handoff.${v}`)}
						</MenuItem>
					))}
				</TextField>
				{form.handoff !== "forbidden" && (
					<div>
						<Typography variant="caption" color="textSecondary">
							{i18n.t("lab.modal.reasonsHelp")}
						</Typography>
						<div>
							{REASONS.map(reason => (
								<FormControlLabel
									key={reason}
									control={
										<Checkbox
											color="primary"
											checked={form.reasons.includes(reason)}
											onChange={() => toggleReason(reason)}
										/>
									}
									label={i18n.t(`lab.reason.${reason}`)}
								/>
							))}
						</div>
					</div>
				)}
				<TextField
					label={i18n.t("lab.modal.mustContain")}
					helperText={i18n.t("lab.modal.termsHelp")}
					value={form.mustContain}
					onChange={e => set({ mustContain: e.target.value })}
					fullWidth
					multiline
					rows={2}
					variant="outlined"
					margin="dense"
				/>
				<TextField
					label={i18n.t("lab.modal.mustNotContain")}
					helperText={i18n.t("lab.modal.termsHelp")}
					value={form.mustNotContain}
					onChange={e => set({ mustNotContain: e.target.value })}
					fullWidth
					multiline
					rows={2}
					variant="outlined"
					margin="dense"
				/>
				<div style={{ display: "flex", gap: 8 }}>
					{[
						["minReplies", "lab.modal.minReplies"],
						["maxReplies", "lab.modal.maxReplies"],
						["maxReplyChars", "lab.modal.maxReplyChars"],
						["replyTimeoutSec", "lab.modal.timeout"],
					].map(([field, label]) => (
						<TextField
							key={field}
							type="number"
							label={i18n.t(label)}
							value={form[field]}
							onChange={e => set({ [field]: e.target.value })}
							variant="outlined"
							margin="dense"
							inputProps={{ min: 0 }}
						/>
					))}
				</div>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="secondary" variant="outlined" disabled={saving}>
					{i18n.t("lab.buttons.cancel")}
				</Button>
				<Button onClick={save} color="primary" variant="contained" disabled={saving}>
					{i18n.t("lab.buttons.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ScenarioModal;
